import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { getSceneGuestProductModelData } from '../../actions/sceneGuestModel.actions'

const normalizeIds = (values = []) => (
    [...new Set(
        (Array.isArray(values) ? values : [])
            .map((value) => String(value?._id || value || '').trim())
            .filter((value) => /^[a-f\d]{24}$/i.test(value))
    )]
)

const scheduleIdle = (callback, timeout = 1400) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(callback, { timeout })
        return () => window.cancelIdleCallback?.(id)
    }

    const id = window.setTimeout(callback, 180)
    return () => window.clearTimeout(id)
}

const waitForIdle = () => new Promise((resolve) => {
    scheduleIdle(resolve)
})


const getInitialWorkerCount = (queueLength) => {
    if (typeof navigator === 'undefined') return 1

    const cores = Math.max(1, Number(navigator.hardwareConcurrency || 4))
    const memory = Number(navigator.deviceMemory || 0)
    const highCapacityDevice = cores >= 8 && (!memory || memory >= 8)

    return Math.min(queueLength, highCapacityDevice ? 2 : 1)
}

const SceneGuestModelsLoader = ({
    guestToken,
    initialProductIds = [],
    deferredProductIds = [],
    onProgress,
}) => {
    const dispatch = useDispatch()
    const store = useStore()
    const requestedRef = useRef(new Set())
    const failedRef = useRef(new Set())
    const generationRef = useRef(0)
    const progressRef = useRef('')

    const initialIds = useMemo(
        () => normalizeIds(initialProductIds),
        [initialProductIds]
    )
    const deferredIds = useMemo(() => {
        const initialSet = new Set(initialIds)
        return normalizeIds(deferredProductIds).filter((id) => !initialSet.has(id))
    }, [deferredProductIds, initialIds])
    const initialKey = initialIds.join('|')
    const deferredKey = deferredIds.join('|')

    useEffect(() => {
        requestedRef.current.clear()
        failedRef.current.clear()
        progressRef.current = ''
        generationRef.current += 1
    }, [deferredKey, guestToken, initialKey])

    useEffect(() => {
        const generation = generationRef.current + 1
        generationRef.current = generation
        let cancelled = false
        let cancelIdle = null

        const allIds = [...initialIds, ...deferredIds]
        const total = allIds.length
        const getModelsData = () => store.getState()?.office?.modelsData || {}
        let loaded = allIds.filter((id) => !!getModelsData()?.[id]).length
        let failed = allIds.filter((id) => failedRef.current.has(id)).length

        const report = (phase, loading) => {
            if (cancelled || generationRef.current !== generation) return

            const payload = {
                phase,
                total,
                loaded,
                failed,
                loading,
            }
            const key = `${phase}:${total}:${loaded}:${failed}:${loading ? 1 : 0}`

            if (progressRef.current === key) return
            progressRef.current = key
            onProgress?.(payload)
        }

        if (!guestToken || !total) {
            report('done', false)
            return undefined
        }

        const isLoaded = (productId) => !!getModelsData()?.[productId]

        const loadOne = async (productId) => {
            if (
                cancelled ||
                generationRef.current !== generation ||
                !productId ||
                isLoaded(productId) ||
                requestedRef.current.has(productId) ||
                failedRef.current.has(productId)
            ) {
                return
            }

            requestedRef.current.add(productId)

            try {
                await dispatch(getSceneGuestProductModelData(guestToken, productId))
                if (!cancelled && generationRef.current === generation) loaded += 1
            } catch (_error) {
                if (!cancelled && generationRef.current === generation) {
                    failed += 1
                    failedRef.current.add(productId)
                }
            }
        }

        const runInitialQueue = async () => {
            const queue = initialIds.filter((id) => (
                !isLoaded(id) &&
                !requestedRef.current.has(id) &&
                !failedRef.current.has(id)
            ))

            if (!queue.length) return

            report('initial', true)

            const workers = getInitialWorkerCount(queue.length)
            let cursor = 0

            const worker = async () => {
                while (!cancelled && generationRef.current === generation) {
                    const index = cursor
                    cursor += 1
                    if (index >= queue.length) return

                    await loadOne(queue[index])
                    report('initial', true)
                }
            }

            await Promise.all(new Array(workers).fill(null).map(worker))
        }

        const runDeferredQueue = async () => {
            const queue = deferredIds.filter((id) => (
                !isLoaded(id) &&
                !requestedRef.current.has(id) &&
                !failedRef.current.has(id)
            ))

            if (!queue.length) {
                report('done', false)
                return
            }

            report('deferred', true)

            for (const productId of queue) {
                if (cancelled || generationRef.current !== generation) return

                await waitForIdle()
                if (cancelled || generationRef.current !== generation) return

                await loadOne(productId)
                report('deferred', true)
            }

            report('done', false)
        }

        const start = async () => {
            await runInitialQueue()

            if (cancelled || generationRef.current !== generation) return

            if (deferredIds.length) {
                cancelIdle = scheduleIdle(runDeferredQueue, 1800)
            } else {
                report('done', false)
            }
        }

        start()

        return () => {
            cancelled = true
            cancelIdle?.()
        }
    }, [
        deferredKey,
        dispatch,
        guestToken,
        initialKey,
        onProgress,
        store,
    ])

    return null
}

export default SceneGuestModelsLoader
